import React, { useState, useEffect } from 'react';
import { apiService, Device } from '../services/api';
import DevicePicker from './DevicePicker';
import {
  Heart,
  Activity as ActivityIcon,
  HardDrive,
  Package,
  Users,
  Network,
  Shield,
  FileCode,
  Save,
  RefreshCw,
  Settings
} from 'lucide-react';

interface AgentSettingsProps {}

interface SettingsFormData {
  heartbeat_interval: string;
  performance_interval: string;
  hardware_interval: string;
  software_interval: string;
  user_interval: string;
  network_interval: string;
  security_interval: string;
  activity_tracking_enabled: boolean;
  activity_check_interval_seconds: number;
  activity_send_interval_seconds: number;
  security_monitoring_enabled: boolean;
  command_poll_interval_seconds: number;
  system_info?: boolean;
  hardware_info?: boolean;
  software_info?: boolean;
  network_info?: boolean;
  process_info?: boolean;
  filesystem_info?: boolean;
  device_identification?: {
    primary_method?: string;
    require_device_id?: boolean;
    fallback_to_registration?: boolean;
  };
  metadata_collection_enabled?: boolean;
  collect_current_user?: boolean;
  collect_serial_number?: boolean;
  collect_hostname?: boolean;
  collect_ip_address?: boolean;
  collect_device_type?: boolean;
  collect_os_version?: boolean;
  metadata_send_in_heartbeat?: boolean;
  heartbeat_include_metadata?: boolean;
}

interface IntervalField {
  key: keyof SettingsFormData;
  label: string;
  description: string;
  help: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  colorClass: string;
  defaultValue: string;
}

const AgentSettings: React.FC<AgentSettingsProps> = () => {
  const [activeTab, setActiveTab] = useState<'global' | 'os' | 'group' | 'device'>('global');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showYaml, setShowYaml] = useState(false);
  const [yamlEditMode, setYamlEditMode] = useState(false);
  
  // Settings form data
  const [settings, setSettings] = useState<SettingsFormData>({
    heartbeat_interval: '5m',
    performance_interval: '24h',
    hardware_interval: '72h',
    software_interval: '48h',
    user_interval: '6h',
    network_interval: '24h',
    security_interval: '12h',
    activity_tracking_enabled: true,
    activity_check_interval_seconds: 5,
    activity_send_interval_seconds: 300,
    security_monitoring_enabled: false,
    command_poll_interval_seconds: 10,
    system_info: true,
    hardware_info: true,
    software_info: true,
    network_info: true,
    process_info: false,
    filesystem_info: false,
    device_identification: {
      primary_method: 'serial_number',
      require_device_id: true,
      fallback_to_registration: false
    },
    metadata_collection_enabled: true,
    collect_current_user: true,
    collect_serial_number: true,
    collect_hostname: true,
    collect_ip_address: true,
    collect_device_type: true,
    collect_os_version: true,
    metadata_send_in_heartbeat: true,
    heartbeat_include_metadata: true
  });

  // OS / Group / Device selection
  const [selectedOs, setSelectedOs] = useState<string>('windows');
  const [groups, setGroups] = useState<any[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  // YAML content (editable)
  const [yamlContent, setYamlContent] = useState<string>('');

  // Interval cards configuration (matches the Settings modal template)
  const intervalFields: IntervalField[] = [
    {
      key: 'heartbeat_interval',
      label: 'Heartbeat Interval',
      description: 'How often agents send heartbeat signals to the server',
      help: 'Recommended: 5m (5 minutes)',
      icon: Heart,
      colorClass: 'text-red-500',
      defaultValue: '5m'
    },
    {
      key: 'performance_interval',
      label: 'Performance Metrics',
      description: 'How often to collect CPU, memory, disk usage data',
      help: 'Recommended: 24h (1 day)',
      icon: ActivityIcon,
      colorClass: 'text-blue-500',
      defaultValue: '24h'
    },
    {
      key: 'hardware_interval',
      label: 'Hardware Inventory',
      description: 'How often to collect hardware information (CPU, RAM, storage)',
      help: 'Recommended: 72h (3 days)',
      icon: HardDrive,
      colorClass: 'text-purple-500',
      defaultValue: '72h'
    },
    {
      key: 'software_interval',
      label: 'Software Inventory',
      description: 'How often to collect installed software and applications',
      help: 'Recommended: 48h (2 days)',
      icon: Package,
      colorClass: 'text-green-500',
      defaultValue: '48h'
    },
    {
      key: 'user_interval',
      label: 'User Inventory',
      description: 'How often to collect user account and login information',
      help: 'Recommended: 6h (6 hours)',
      icon: Users,
      colorClass: 'text-orange-500',
      defaultValue: '6h'
    },
    {
      key: 'network_interval',
      label: 'Network Inventory',
      description: 'How often to collect network configuration and connectivity data',
      help: 'Recommended: 24h (1 day)',
      icon: Network,
      colorClass: 'text-indigo-500',
      defaultValue: '24h'
    },
    {
      key: 'security_interval',
      label: 'Security Inventory',
      description: 'How often to collect security-related information',
      help: 'Recommended: 12h (12 hours)',
      icon: Shield,
      colorClass: 'text-yellow-500',
      defaultValue: '12h'
    }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'global') {
      loadGlobalSettings();
    } else if (activeTab === 'os' && selectedOs) {
      loadOsSettings();
    } else if (activeTab === 'group' && selectedGroupId) {
      loadGroupSettings();
    } else if (activeTab === 'device' && selectedDeviceId) {
      loadDeviceSettings();
    }
  }, [activeTab, selectedOs, selectedGroupId, selectedDeviceId]);

  const loadInitialData = async () => {
    try {
      // Load groups
      const groupsRes = await apiService.getDeviceGroups();
      setGroups((groupsRes as any).items || []);
      
      // Load devices
      const devicesRes = await apiService.getDevices();
      setDevices(devicesRes || []);
      
      // Load global settings
      await loadGlobalSettings();
    } catch (err) {
      console.error('Failed to load initial data:', err);
    }
  };

  const loadGlobalSettings = async () => {
    try {
      const response = await apiService.getCollectionSettings();
      if (response.success && response.data?.settings) {
        setSettings({
          heartbeat_interval: response.data.settings.heartbeat_interval || '5m',
          performance_interval: response.data.settings.performance_interval || '24h',
          hardware_interval: response.data.settings.hardware_interval || '72h',
          software_interval: response.data.settings.software_interval || '48h',
          user_interval: response.data.settings.user_interval || '6h',
          network_interval: response.data.settings.network_interval || '24h',
          security_interval: response.data.settings.security_interval || '12h',
          activity_tracking_enabled: response.data.settings.activity_tracking_enabled !== false,
          activity_check_interval_seconds: response.data.settings.activity_check_interval_seconds || 5,
          activity_send_interval_seconds: response.data.settings.activity_send_interval_seconds || 300,
          security_monitoring_enabled: response.data.settings.security_monitoring_enabled === true,
          command_poll_interval_seconds: response.data.settings.command_poll_interval_seconds || 10,
          system_info: response.data.settings.system_info !== false,
          hardware_info: response.data.settings.hardware_info !== false,
          software_info: response.data.settings.software_info !== false,
          network_info: response.data.settings.network_info !== false,
          process_info: response.data.settings.process_info === true,
          filesystem_info: response.data.settings.filesystem_info === true,
          device_identification: response.data.settings.device_identification || {
            primary_method: 'serial_number',
            require_device_id: true,
            fallback_to_registration: false
          },
          metadata_collection_enabled: response.data.settings.metadata_collection_enabled !== false,
          collect_current_user: response.data.settings.collect_current_user !== false,
          collect_serial_number: response.data.settings.collect_serial_number !== false,
          collect_hostname: response.data.settings.collect_hostname !== false,
          collect_ip_address: response.data.settings.collect_ip_address !== false,
          collect_device_type: response.data.settings.collect_device_type !== false,
          collect_os_version: response.data.settings.collect_os_version !== false,
          metadata_send_in_heartbeat: response.data.settings.metadata_send_in_heartbeat !== false,
          heartbeat_include_metadata: response.data.settings.heartbeat_include_metadata !== false
        });
      }
      await updateYamlPreview();
    } catch (err) {
      console.error('Failed to load global settings:', err);
    }
  };

  const loadGroupSettings = async () => {
    if (!selectedGroupId) return;
    try {
      const response = await apiService.getGroupCollectionSettings(selectedGroupId);
      if (response.success && response.data?.settings) {
        setSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
      } else {
        await loadGlobalSettings();
      }
      await updateYamlPreview();
    } catch (err) {
      console.error('Failed to load group settings:', err);
    }
  };

  const loadDeviceSettings = async () => {
    if (!selectedDeviceId) return;
    try {
      const response = await apiService.getDeviceCollectionSettings(selectedDeviceId);
      if (response.success && response.data?.settings) {
        setSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
      } else {
        await loadGlobalSettings();
      }
      await updateYamlPreview();
    } catch (err) {
      console.error('Failed to load device settings:', err);
    }
  };

  const loadOsSettings = async () => {
    if (!selectedOs) return;
    try {
      const response = await apiService.getOsCollectionSettings(selectedOs);
      if (response.success && response.data?.settings) {
        setSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
      } else {
        await loadGlobalSettings();
      }
      await updateYamlPreview();
    } catch (err) {
      console.error('Failed to load OS settings:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      let response;
      if (activeTab === 'global') {
        response = await apiService.updateCollectionSettings(settings);
      } else if (activeTab === 'os' && selectedOs) {
        response = await apiService.updateOsCollectionSettings(selectedOs, settings, 'admin');
      } else if (activeTab === 'group' && selectedGroupId) {
        response = await apiService.updateGroupCollectionSettings(selectedGroupId, settings, 'admin');
      } else if (activeTab === 'device' && selectedDeviceId) {
        response = await apiService.updateDeviceCollectionSettings(selectedDeviceId, settings, 'admin');
      } else {
        setError('Please select a group or device');
        return;
      }

      if (response.success) {
        setSuccess('Settings saved successfully! Agents will pick up changes on next config fetch.');
        await updateYamlPreview();
      } else {
        setError('Failed to save settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveYaml = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Parse YAML and convert to settings object
      // Simple YAML parser (for basic key-value pairs)
      const lines = yamlContent.split('\n');
      const parsedSettings: any = {};
      
      let currentSection = '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) continue;
        
        if (trimmed.endsWith(':')) {
          currentSection = trimmed.slice(0, -1).trim();
          continue;
        }
        
        const match = trimmed.match(/^(\w+):\s*(.+)$/);
        if (match) {
          const key = match[1];
          let value: any = match[2].trim();
          
          // Remove quotes if present
          if ((value.startsWith('"') && value.endsWith('"')) || 
              (value.startsWith("'") && value.endsWith("'"))) {
            value = value.slice(1, -1);
          }
          
          // Convert boolean strings
          if (value === 'true') value = true;
          else if (value === 'false') value = false;
          // Convert numbers
          else if (/^\d+$/.test(value)) value = parseInt(value);
          
          // Map YAML keys to settings keys
          if (currentSection === 'collection') {
            if (key === 'interval') parsedSettings.heartbeat_interval = value;
            else parsedSettings[`${key}_interval`] = value;
          } else if (currentSection === 'activity_tracking') {
            parsedSettings[`activity_${key}`] = value;
          } else if (currentSection === 'security_monitoring') {
            parsedSettings[`security_monitoring_${key}`] = value;
          } else if (currentSection === 'command_execution') {
            parsedSettings[`command_${key}`] = value;
          }
        }
      }

      // Save using the same logic as handleSave
      let response;
      if (activeTab === 'global') {
        response = await apiService.updateCollectionSettings({ ...settings, ...parsedSettings });
      } else if (activeTab === 'os' && selectedOs) {
        response = await apiService.updateOsCollectionSettings(selectedOs, { ...settings, ...parsedSettings }, 'admin');
      } else if (activeTab === 'group' && selectedGroupId) {
        response = await apiService.updateGroupCollectionSettings(selectedGroupId, { ...settings, ...parsedSettings }, 'admin');
      } else if (activeTab === 'device' && selectedDeviceId) {
        response = await apiService.updateDeviceCollectionSettings(selectedDeviceId, { ...settings, ...parsedSettings }, 'admin');
      } else {
        setError('Please select a group or device');
        return;
      }

      if (response.success) {
        setSuccess('YAML configuration saved successfully!');
        setYamlEditMode(false);
        // Reload settings to reflect changes
        if (activeTab === 'global') await loadGlobalSettings();
        else if (activeTab === 'os') await loadOsSettings();
        else if (activeTab === 'group') await loadGroupSettings();
        else if (activeTab === 'device') await loadDeviceSettings();
      } else {
        setError('Failed to save YAML configuration');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse or save YAML');
    } finally {
      setSaving(false);
    }
  };

  const updateYamlPreview = async () => {
    try {
      // Generate YAML from current settings (for the active tab level)
      const yaml = `collection:
  enabled: true
  interval: "${settings.heartbeat_interval || '5m'}"
  performance_interval: "${settings.performance_interval || '24h'}"
  hardware_interval: "${settings.hardware_interval || '72h'}"
  software_interval: "${settings.software_interval || '48h'}"
  user_interval: "${settings.user_interval || '6h'}"
  network_interval: "${settings.network_interval || '24h'}"
  security_interval: "${settings.security_interval || '12h'}"

activity_tracking:
  enabled: ${settings.activity_tracking_enabled ?? true}
  check_interval_seconds: ${settings.activity_check_interval_seconds ?? 5}
  send_interval_seconds: ${settings.activity_send_interval_seconds ?? 300}

security_monitoring:
  enabled: ${settings.security_monitoring_enabled ?? false}

command_execution:
  poll_interval_seconds: ${settings.command_poll_interval_seconds ?? 10}

device_identification:
  primary_method: "${settings.device_identification?.primary_method || 'serial_number'}"
  require_device_id: ${settings.device_identification?.require_device_id !== false}
  fallback_to_registration: ${settings.device_identification?.fallback_to_registration === true}

metadata_collection:
  enabled: ${settings.metadata_collection_enabled !== false}
  collect_current_user: ${settings.collect_current_user !== false}
  collect_serial_number: ${settings.collect_serial_number !== false}
  collect_hostname: ${settings.collect_hostname !== false}
  collect_ip_address: ${settings.collect_ip_address !== false}
  collect_device_type: ${settings.collect_device_type !== false}
  collect_os_version: ${settings.collect_os_version !== false}
  send_in_heartbeat: ${settings.metadata_send_in_heartbeat !== false}
  heartbeat_include_metadata: ${settings.heartbeat_include_metadata !== false}`;
      setYamlContent(yaml);
    } catch (err) {
      console.error('Failed to update YAML preview:', err);
    }
  };

  useEffect(() => {
    updateYamlPreview();
  }, [settings, selectedDeviceId]);

  const handleInputChange = (field: keyof SettingsFormData, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="flex-1 overflow-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
          <Settings size={24} className="text-blue-600" />
          Agent Configuration
        </h1>
        <p className="text-sm text-slate-600">
          Configure agent settings globally, per OS, per group, or per device. Changes are applied automatically when agents fetch their configuration.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b-2 border-slate-200 mb-6">
        <button
          onClick={() => setActiveTab('global')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'global'
              ? 'text-indigo-600 border-b-2 border-indigo-600 -mb-[2px]'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Global Settings
        </button>
        <button
          onClick={() => setActiveTab('os')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'os'
              ? 'text-indigo-600 border-b-2 border-indigo-600 -mb-[2px]'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          OS Settings
        </button>
        <button
          onClick={() => setActiveTab('group')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'group'
              ? 'text-indigo-600 border-b-2 border-indigo-600 -mb-[2px]'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Group Settings
        </button>
        <button
          onClick={() => setActiveTab('device')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'device'
              ? 'text-indigo-600 border-b-2 border-indigo-600 -mb-[2px]'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Device Settings
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Main Content - Full Width */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-6">
          {/* Header with YAML Toggle */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-slate-800">
              {activeTab === 'global'
                ? 'Global Configuration'
                : activeTab === 'os'
                ? 'OS Configuration'
                : activeTab === 'group'
                ? 'Group Configuration'
                : 'Device Configuration'}
            </h2>
            <button
              onClick={() => {
                setShowYaml(!showYaml);
                if (!showYaml) {
                  updateYamlPreview();
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
            >
              <FileCode size={16} />
              {showYaml ? 'Hide YAML' : 'View/Edit YAML'}
            </button>
          </div>

          {/* YAML Editor (when toggled) */}
          {showYaml && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">YAML Configuration</h3>
                  <p className="text-xs text-slate-500">
                    {activeTab === 'global' 
                      ? 'Global settings (applied to all devices)'
                      : activeTab === 'os'
                      ? `OS-specific settings for ${selectedOs}`
                      : activeTab === 'group'
                      ? `Group-specific settings`
                      : 'Device-specific settings'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {yamlEditMode ? (
                    <>
                      <button
                        onClick={handleSaveYaml}
                        disabled={saving}
                        className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-medium disabled:opacity-50"
                      >
                        <Save size={14} />
                        Save YAML
                      </button>
                      <button
                        onClick={() => {
                          setYamlEditMode(false);
                          updateYamlPreview();
                        }}
                        className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded text-sm font-medium"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setYamlEditMode(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded text-sm font-medium"
                    >
                      <FileCode size={14} />
                      Edit YAML
                    </button>
                  )}
                </div>
              </div>
              {yamlEditMode ? (
                <textarea
                  value={yamlContent}
                  onChange={(e) => setYamlContent(e.target.value)}
                  className="w-full h-64 p-3 bg-white border border-slate-300 rounded font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  spellCheck={false}
                />
              ) : (
                <pre className="w-full h-64 p-3 bg-white border border-slate-300 rounded overflow-auto text-sm font-mono text-slate-700">
                  {yamlContent || 'Loading preview...'}
                </pre>
              )}
            </div>
          )}

          {/* OS / Group / Device Selector */}
          {activeTab === 'os' && (
            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-slate-700">
                Select Operating System
              </label>
              <select
                value={selectedOs}
                onChange={(e) => setSelectedOs(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="windows">Windows</option>
                <option value="linux">Linux</option>
                <option value="macos">macOS</option>
                <option value="android">Android</option>
                <option value="ios">iOS</option>
              </select>
            </div>
          )}

          {activeTab === 'group' && (
            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-slate-700">
                Select Group
              </label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">-- Select a group --</option>
                {groups.map(group => (
                  <option key={group.group_id || group.id} value={group.group_id || group.id}>
                    {group.name || group.group_id || group.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          {activeTab === 'device' && (
            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-slate-700">
                Select Device
              </label>
              <DevicePicker
                devices={devices}
                value={selectedDeviceId}
                onChange={setSelectedDeviceId}
              />
            </div>
          )}

          {/* Collection Intervals - Template-style cards */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-slate-700 mb-4">Data Collection Frequencies</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {intervalFields.map((field) => {
                const Icon = field.icon;
                const value = settings[field.key] as string;
                return (
                  <div
                    key={field.key as string}
                    className="bg-slate-50 rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg bg-white ${field.colorClass}`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-800 mb-1">{field.label}</div>
                        <div className="text-xs text-slate-600 mb-2">{field.description}</div>
                      </div>
                    </div>
                    <div>
                      <input
                        type="text"
                        value={value || field.defaultValue}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        placeholder={field.defaultValue}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <div className="mt-1 text-xs text-slate-500">{field.help}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Activity Tracking */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h3 className="text-base font-semibold text-slate-700 mb-4">Activity Tracking</h3>
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.activity_tracking_enabled}
                  onChange={(e) => handleInputChange('activity_tracking_enabled', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700">Enable Activity Tracking</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-sm text-slate-600">Check Interval (seconds)</label>
                <input
                  type="number"
                  value={settings.activity_check_interval_seconds}
                  onChange={(e) => handleInputChange('activity_check_interval_seconds', parseInt(e.target.value) || 5)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-slate-600">Send Interval (seconds)</label>
                <input
                  type="number"
                  value={settings.activity_send_interval_seconds}
                  onChange={(e) => handleInputChange('activity_send_interval_seconds', parseInt(e.target.value) || 300)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Security & Command Execution */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h3 className="text-base font-semibold text-slate-700 mb-4">Security & Commands</h3>
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.security_monitoring_enabled}
                  onChange={(e) => handleInputChange('security_monitoring_enabled', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700">Enable Security Monitoring</span>
              </label>
            </div>
            <div>
              <label className="block mb-1 text-sm text-slate-600">Command Poll Interval (seconds)</label>
              <input
                type="number"
                value={settings.command_poll_interval_seconds}
                onChange={(e) => handleInputChange('command_poll_interval_seconds', parseInt(e.target.value) || 10)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Metadata Collection Settings */}
          <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="text-base font-semibold text-slate-700 mb-4">Metadata Collection</h3>
            <p className="text-xs text-slate-600 mb-4">
              Configure what device information agents should collect and send to the server.
            </p>
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.metadata_collection_enabled !== false}
                  onChange={(e) => handleInputChange('metadata_collection_enabled', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700 font-medium">Enable Metadata Collection</span>
              </label>
              <p className="ml-6 mt-1 text-xs text-slate-500">
                Master switch for all metadata collection. When disabled, agents won't collect any metadata.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.collect_current_user !== false}
                  onChange={(e) => handleInputChange('collect_current_user', e.target.checked)}
                  disabled={settings.metadata_collection_enabled === false}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-50"
                />
                <label className="text-sm text-slate-700 cursor-pointer">
                  Current User
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.collect_serial_number !== false}
                  onChange={(e) => handleInputChange('collect_serial_number', e.target.checked)}
                  disabled={settings.metadata_collection_enabled === false}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-50"
                />
                <label className="text-sm text-slate-700 cursor-pointer">
                  Serial Number
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.collect_hostname !== false}
                  onChange={(e) => handleInputChange('collect_hostname', e.target.checked)}
                  disabled={settings.metadata_collection_enabled === false}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-50"
                />
                <label className="text-sm text-slate-700 cursor-pointer">
                  Hostname
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.collect_ip_address !== false}
                  onChange={(e) => handleInputChange('collect_ip_address', e.target.checked)}
                  disabled={settings.metadata_collection_enabled === false}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-50"
                />
                <label className="text-sm text-slate-700 cursor-pointer">
                  IP Address
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.collect_os_version !== false}
                  onChange={(e) => handleInputChange('collect_os_version', e.target.checked)}
                  disabled={settings.metadata_collection_enabled === false}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-50"
                />
                <label className="text-sm text-slate-700 cursor-pointer">
                  OS Version (Windows 10/11, Home/Pro)
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.collect_device_type !== false}
                  onChange={(e) => handleInputChange('collect_device_type', e.target.checked)}
                  disabled={settings.metadata_collection_enabled === false}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-50"
                />
                <label className="text-sm text-slate-700 cursor-pointer">
                  Device Type (Laptop/Desktop)
                </label>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-green-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.heartbeat_include_metadata !== false}
                  onChange={(e) => handleInputChange('heartbeat_include_metadata', e.target.checked)}
                  disabled={settings.metadata_collection_enabled === false}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-50"
                />
                <span className="text-sm text-slate-700">Include Metadata in Heartbeat</span>
              </label>
              <p className="ml-6 mt-1 text-xs text-slate-500">
                Send collected metadata with each heartbeat (recommended for real-time updates).
              </p>
            </div>
          </div>

          {/* Device Identification Settings */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-base font-semibold text-slate-700 mb-4">Device Identification</h3>
            <p className="text-xs text-slate-600 mb-4">
              Configure how agents identify themselves to prevent duplicate device registrations on restart.
            </p>
            <div className="mb-4">
              <label className="block mb-1 text-sm text-slate-600">Primary Identification Method</label>
              <select
                value={settings.device_identification?.primary_method || 'serial_number'}
                onChange={(e) => handleInputChange('device_identification', {
                  ...settings.device_identification,
                  primary_method: e.target.value
                })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="serial_number">Serial Number (Most Reliable)</option>
                <option value="platform_device_id">Platform Device ID</option>
                <option value="hostname">Hostname</option>
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Backend will use this method to match existing devices and prevent duplicates.
              </p>
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.device_identification?.require_device_id !== false}
                  onChange={(e) => handleInputChange('device_identification', {
                    ...settings.device_identification,
                    require_device_id: e.target.checked
                  })}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700">Require Device ID from Config</span>
              </label>
              <p className="mt-1 ml-6 text-xs text-slate-500">
                Agent must use device_id from agent.yaml config file. Prevents re-registration on restart.
              </p>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.device_identification?.fallback_to_registration === true}
                  onChange={(e) => handleInputChange('device_identification', {
                    ...settings.device_identification,
                    fallback_to_registration: e.target.checked
                  })}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700">Allow Registration if Device ID Missing</span>
              </label>
              <p className="mt-1 ml-6 text-xs text-slate-500">
                If device_id is not in config file, allow agent to register (not recommended for production).
              </p>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || (activeTab === 'group' && !selectedGroupId) || (activeTab === 'device' && !selectedDeviceId)}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentSettings;
